import HttpMethods from "@common/enums/api/http-methods";
import OrganizationRole from "../enums/organization-roles";
import { ApiKeyScope } from "./external-api";

export const SCOPE_EXTENSION = "x-requiredScope";
export const ROLE_EXTENSION = "x-requiredOrganizationRole";

export interface OpenApiOperation {
  operationId?: string;
  summary?: string;
  "x-requiredScope"?: ApiKeyScope;
  "x-requiredOrganizationRole"?: OrganizationRole;
}

export interface OpenApiDocument {
  openapi: string;
  info: { title: string; version: string; description?: string };
  servers?: { url: string; description?: string }[];
  paths: Record<string, Record<string, OpenApiOperation>>;
}

/** One operation, flattened into what a card needs to render itself. */
export interface ExternalApiEndpoint {
  id: string;
  method: HttpMethods;
  path: string;
  url: string;
  /** The document's own English name, used only when a translation is missing. */
  summary: string;
  requiredScope: ApiKeyScope | null;
  requiredRole: OrganizationRole | null;
}

const HTTP_METHODS: string[] = Object.values(HttpMethods);

/** `listRobots` -> `list-robots`, so an operation id maps onto a translation key. */
export const kebabCase = (value: string): string =>
  value.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();

export const endpointTitleKey = (endpoint: ExternalApiEndpoint): string =>
  `title-api-endpoint-${kebabCase(endpoint.id)}`;

export const endpointDescriptionKey = (endpoint: ExternalApiEndpoint): string =>
  `info-api-endpoint-${kebabCase(endpoint.id)}`;

/** `robots:read` -> `label-api-scope-robots-read`. */
export const scopeLabelKey = (scope: ApiKeyScope): string =>
  `label-api-scope-${scope.replace(":", "-")}`;

/** Every operation in the document, in the order the document lists them. */
export const listEndpoints = (document: OpenApiDocument | null): ExternalApiEndpoint[] => {
  if (document == null) {
    return [];
  }

  const serverUrl = document.servers?.[0]?.url ?? "";

  return Object.entries(document.paths ?? {}).flatMap(([path, operations]) =>
    Object.entries(operations ?? {})
      .filter(([method]) => HTTP_METHODS.includes(method))
      .map(([method, operation]) => ({
        id: operation.operationId ?? `${method}-${path}`,
        method: method as HttpMethods,
        path,
        url: `${serverUrl}${path}`,
        summary: operation.summary ?? path,
        requiredScope: operation[SCOPE_EXTENSION] ?? null,
        requiredRole: operation[ROLE_EXTENSION] ?? null,
      }))
  );
};

export const isEndpointVisibleToRole = (
  endpoint: ExternalApiEndpoint,
  role: OrganizationRole | null | undefined
): boolean => endpoint.requiredRole == null || endpoint.requiredRole === role;


export const isEndpointCallableWithScopes = (
  endpoint: ExternalApiEndpoint,
  grantedScopes: ApiKeyScope[]
): boolean => endpoint.requiredScope == null || grantedScopes.includes(endpoint.requiredScope);

export default OpenApiDocument;

// mock data

/** A trim of the served document down to the fields the list reads. */
export const mockExternalApiSchema: OpenApiDocument = {
  openapi: "3.0.3",
  info: { title: "Kemaro External API", version: "1.0.0" },
  servers: [{ url: "http://localhost:8000/api/v1.0", description: "The api root." }],
  paths: {
    "/external-api/robots/": {
      get: {
        operationId: "listRobots",
        summary: "List robots",
        "x-requiredScope": ApiKeyScope.ROBOTS_READ,
      },
    },
    "/external-api/missions/": {
      get: {
        operationId: "listMissions",
        summary: "List missions",
        "x-requiredScope": ApiKeyScope.MISSIONS_READ,
      },
    },
    "/external-api/robots/provision": {
      post: {
        operationId: "provisionRobot",
        summary: "Register a new robot",
        "x-requiredScope": ApiKeyScope.ROBOTS_PROVISION,
        "x-requiredOrganizationRole": OrganizationRole.MANUFACTURER,
      },
    },
  },
};
